import { Controller, FieldValues, RegisterOptions, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import Routes from 'shared/enums/routes';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';

import { useSystemsContextProvider } from 'features/systems/context/systems-provider';
import { SystemType } from 'features/systems/domain/models/system';
import useSystemInformation from 'features/systems/hooks/system-information-hook';
import IS32ScanConfiguration from 'features/systems/IS32/views/IS32-scan-configuration';
import IS32SystemEventPopup from 'features/systems/IS32/views/IS32-system-event-popup';
import styles from 'features/systems/views/details/system-information.module.scss';
import SystemMonitoringAlerts from 'features/systems/views/details/system-monitoring-alerts';
import IpUnitFunctionalities from './components/ip-unit-functionalities/ip-unit-functionalities';

const SystemInformation = (): React.ReactElement => {
  const { t } = useTranslation('systems');

  const { systemsDetailsHook } = useSystemsContextProvider();
  const { validateName } = useSystemInformation();

  const { hasPermission } = usePermissionAuthorization();

  const navigate = useNavigate();

  const {
    formState: { errors },
    control,
  } = useFormContext();

  const { form, selectedSystem, viewingMode } = systemsDetailsHook;

  const watchSystemType = form.watch('type');
  const isViewing = viewingMode === 'viewing';
  const isEditing = viewingMode === 'editing';

  const hasSelectedType = SystemType.Unknown !== watchSystemType;
  const showNoteInView = isViewing && selectedSystem?.note;
  const showStatusInView = isViewing || isEditing;

  const registerOptions = {
    name: {
      required: t('details.information.requiredHelperText'),
      validate: async (): Promise<boolean | string> => {
        const systemName = form.getValues('name')?.trim();
        if (!systemName) {
          return false;
        }

        return await validateName(systemName, selectedSystem?.id);
      },
    },
  };

  const textField = (
    name: string,
    label: string,
    multiline = false,
    rules: Omit<RegisterOptions<FieldValues, string>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'> | undefined = undefined,
  ): React.ReactElement => (
    <div className={styles.informationItem}>
      <Typography variant="h5">{label}</Typography>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => (
          <TextField
            {...field}
            slotProps={{
              htmlInput: { maxLength: 255 },
            }}
            id={`${name}Input`}
            variant="outlined"
            error={!!errors[name]}
            helperText={errors[name]?.message?.toString()}
            multiline={multiline}
            minRows={multiline ? 8 : undefined}
            maxRows={multiline ? 12 : undefined}
            fullWidth
          />
        )}
      />
    </div>
  );

  const readOnlyField = (label: string, value: string, respectTextFormat = false): React.ReactElement => (
    <div className={styles.informationItem}>
      <Typography variant="h5">{label}</Typography>
      <Typography variant="subtitle1" className={`${respectTextFormat && styles.textFormat}`}>
        {value}
      </Typography>
    </div>
  );

  const communicationDate = (lastCommunicationDate?: Date): string =>
    lastCommunicationDate
      ? t('details.information.statusLastCommunicationDateLabel', {
          lastCommunicationDate: moment(lastCommunicationDate).format('DD-MM-YYYY HH:mm').toString(),
        })
      : t('details.information.statusUnknownLabel');

  return (
    <div className={`${styles.container} ${isViewing && styles.viewing}`}>
      <Typography variant="h2">{isViewing ? selectedSystem?.name : t('details.information.editTitle')}</Typography>

      <SystemMonitoringAlerts system={selectedSystem} />

      {readOnlyField(t('details.information.typeLabel'), t(`details.information.type.${selectedSystem?.type}`))}

      {selectedSystem?.type === SystemType.IPUnit &&
        readOnlyField(t('details.information.unitTypeLabel'), t(`details.information.unitType.${selectedSystem?.unitType}`))}

      {isViewing && (
        <>
          <div className={styles.informationItem}>
            <Typography variant="h5">{t('details.information.connectionLabel')}</Typography>
            <Typography
              variant="subtitle1"
              className={styles.linkButton}
              onClick={() => {
                navigate(`${Routes.Integrations}/${Routes.IntegrationsGateways}?id=${selectedSystem?.gateway.id}`);
              }}
            >
              {selectedSystem?.gateway?.name ?? ''}
            </Typography>
          </div>

          {selectedSystem?.type === SystemType.IS32 &&
            readOnlyField(
              t('details.information.audioLabel'),
              selectedSystem?.audioAvailable ? t('details.information.yes') : t('details.information.no'),
            )}

          {selectedSystem?.type === SystemType.IPUnit && <IpUnitFunctionalities system={selectedSystem} />}
        </>
      )}

      {hasSelectedType && (
        <>
          {isEditing && textField('name', t('details.information.nameLabel'), false, registerOptions.name)}

          {readOnlyField(t('details.information.macAddressLabel'), selectedSystem?.macAddress ?? '')}

          {selectedSystem?.ipAddress && readOnlyField(t('details.information.ipAddressLabel'), selectedSystem?.ipAddress)}

          {isEditing && textField('note', t('details.information.noteLabel'), true)}
          {showNoteInView && readOnlyField(t('details.information.noteLabel'), selectedSystem.note ?? '', true)}

          {showStatusInView &&
            readOnlyField(t('details.information.statusLabel'), communicationDate(selectedSystem?.lastCommunicationDate))}

          {selectedSystem?.firmwareVersion &&
            readOnlyField(t('details.information.firmwareVersionLabel'), selectedSystem?.firmwareVersion ?? '')}

          {selectedSystem?.type === SystemType.IS32 && isViewing && hasPermission(Permission.UpdateSystems) && (
            <>
              <IS32ScanConfiguration />
              <IS32SystemEventPopup />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SystemInformation;
